"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.numberService = void 0;
const RoundType_1 = require("./RoundType");
const GlobalizeService_1 = require("./GlobalizeService");
/**
 * Implementation of a Service that can be used for number parsing and rounding.
 */
class _NumberService {
    constructor() { }
    /**
     * Round a given value to the given precision. The value is rounded down.
     * Before the rounding takes place the value is truncated to the significant precision.
     *
     * @param value The given value.
     * @param precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * @param significantPrecision The number of decimal digits that are taken into account during rounding.
     * The default value is equal to precision + 1.
     * @returns The rounded value
     */
    floor(value, precision, significantPrecision) {
        const { precisionScale, significantPrecisionScale } = this.getPrecisionScales(precision, significantPrecision);
        const scaledValue = value >= 0
            ? Math.floor(value * precisionScale * significantPrecisionScale) / significantPrecisionScale
            : Math.ceil(value * precisionScale * significantPrecisionScale) / significantPrecisionScale;
        const result = Math.floor(scaledValue) / precisionScale;
        return result;
    }
    /**
     * Round a given value to the given precision. The value is rounded up.
     * Before the rounding takes place the value is truncated to the significant precision.
     *
     * @param value The given value.
     * @param precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * @param significantPrecision The number of decimal digits that are taken into account during rounding
     * The default value is equal to precision + 1.
     * @returns The rounded value
     */
    ceil(value, precision, significantPrecision) {
        const { precisionScale, significantPrecisionScale } = this.getPrecisionScales(precision, significantPrecision);
        const scaledValue = value >= 0
            ? Math.floor(value * precisionScale * significantPrecisionScale) / significantPrecisionScale
            : Math.ceil(value * precisionScale * significantPrecisionScale) / significantPrecisionScale;
        return Math.ceil(scaledValue) / precisionScale;
    }
    /**
     * Round a given value to the given precision. The value is rounded.
     * Before the rounding takes place the value is truncated to the significant precision.
     *
     * @param value The given value.
     * @param precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * @param significantPrecision The number of decimal digits that are taken into account during rounding
     * The default value is equal to precision + 1.
     * @returns The rounded value
     */
    round(value, precision, significantPrecision) {
        const { precisionScale, significantPrecisionScale } = this.getPrecisionScales(precision, significantPrecision);
        const scaledValue = value >= 0
            ? Math.floor(value * precisionScale * significantPrecisionScale) / significantPrecisionScale
            : Math.ceil(value * precisionScale * significantPrecisionScale) / significantPrecisionScale;
        return Math.round(scaledValue) / precisionScale;
    }
    /**
     * A method that parses localized number representations and rounds them according to the given precision and round type.
     *
     * @param value Could be a number, a localized number string or an object with a toString() method that returns a localized number string.
     * @param locale The locale that is used to interpret the number string.
     * @param roundType The round type that should be applied during the round operation.
     * The default round type is ROUND.
     * @param precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * @param significantPrecision The number of digits after the decimal point that are considered during rounding.
     * The default value is equal to precision + 1.
     * @returns Returns the rounded number or undefined if the value couldn't be parsed.
     */
    toNumber(value, locale, roundType, precision, significantPrecision) {
        let result;
        // if value exist
        if (value || value === 0) {
            let unroundedNumber;
            // if value is a number
            if (typeof value === 'number') {
                unroundedNumber = value;
            }
            else {
                unroundedNumber = this.parseNumber(value, locale);
            }
            result = this.roundNumber(unroundedNumber, roundType, precision, significantPrecision);
        }
        return result;
    }
    /**
     * Parses a localized number string or an object with a toString() method that returns a localized number string.
     *
     * @param value A localized number string or an object with a toString() method that returns a localized number string.
     * @param locale The locale that is used to interpret the number string.
     * @returns Returns the parsed number or undefined if the number string couldn't be parsed.
     */
    parseNumber(value, locale) {
        let result;
        if ((value || value === 0) && value.toString && locale) {
            if (Number.isFinite(value)) {
                result = value;
            }
            else {
                result = GlobalizeService_1.globalizeService.localizedStringToNumber(value.toString(), locale);
            }
        }
        return result;
    }
    /**
     * Round a number according to the given precision and round type.
     *
     * @param value The number that should be rounded.
     * @param roundType The given round type.
     * The default round type is ROUND.
     * @param precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * @param significantPrecision The number of digits after the decimal point that are considered during rounding.
     * The default value is equal to precision + 1.
     * @returns Returns the rounded number or undefined if the given number is equal to NaN or Infinity.
     */
    roundNumber(value, roundType, precision, significantPrecision) {
        let result;
        if (Number.isFinite(value)) {
            switch (roundType) {
                case RoundType_1.RoundType.FLOOR:
                    result = this.floor(value, precision, significantPrecision);
                    break;
                case RoundType_1.RoundType.CEIL:
                    result = this.ceil(value, precision, significantPrecision);
                    break;
                case RoundType_1.RoundType.ROUND:
                    result = this.round(value, precision, significantPrecision);
                    break;
                case RoundType_1.RoundType.FLOOR_ABSOLUTE_VALUE:
                    result =
                        value >= 0
                            ? this.floor(value, precision, significantPrecision)
                            : this.ceil(value, precision, significantPrecision);
                    break;
                case RoundType_1.RoundType.CEIL_ABSOLUTE_VALUE:
                    result =
                        value >= 0
                            ? this.ceil(value, precision, significantPrecision)
                            : this.floor(value, precision, significantPrecision);
                    break;
                default:
                    result = this.round(value, precision, significantPrecision);
                    break;
            }
        }
        return result;
    }
    /**
     * Converts a number to localized number string with the given precision.
     *
     * @param value The given number.
     * @param locale The locale that should be used for the localization.
     * @param hasGroupingSeparators Should grouping (thousand) separators be added to the localized string.
     * The default value is true.
     * @param roundType The given round type.
     * The default round type is ROUND.
     * @param precision The desired number of digits after the decimal point that the string should have.
     * The default value is 0.
     * @param significantPrecision The number of digits after the decimal point that are considered during rounding.
     * The default value is equal to precision + 1.
     * @returns
     */
    toLocalizedString(value, locale, hasGroupingSeparators, roundType, precision, significantPrecision) {
        const roundedValue = this.roundNumber(value, roundType, precision, significantPrecision);
        const hasGrouping = hasGroupingSeparators !== false;
        const maxDigits = precision ? precision : 0;
        const localizedNumber = Number.isFinite(roundedValue)
            ? roundedValue.toLocaleString(locale, {
                useGrouping: hasGrouping,
                maximumFractionDigits: maxDigits,
            })
            : undefined;
        return localizedNumber;
    }
    getPrecisionScales(precision, significantPrecision) {
        const precisionValue = this.getPrecisionValue(precision);
        const significantPrecisionValue = this.getSignificantPrecisionValue(precisionValue, significantPrecision);
        const precisionScale = Math.pow(10, precisionValue);
        const significantPrecisionScale = Math.pow(10, significantPrecisionValue - precisionValue);
        return { precisionScale, significantPrecisionScale };
    }
    getPrecisionValue(precision) {
        return (Number.isFinite(precision) ? precision : 0);
    }
    getSignificantPrecisionValue(precisionValue, significantPrecision) {
        return (Number.isFinite(significantPrecision) && (significantPrecision !== null && significantPrecision !== void 0 ? significantPrecision : NaN) >= precisionValue
            ? significantPrecision
            : precisionValue + 1);
    }
}
/**
 * The exported number service instance.
 *
 * @type {NumberService}
 */
exports.numberService = new _NumberService();
//# sourceMappingURL=NumberService.js.map